import { type CSSProperties, useCallback, useRef } from 'react';

import {
  CircularProgress,
  Box as Flex,
  Tooltip,
  styled,
} from '@material-ui/core';

import {
  RiAlignItemHorizontalCenterLine,
  RiAlignItemLeftLine,
  RiAlignItemRightLine,
  RiDeleteBinLine,
  RiImageAddLine,
} from '@remixicon/react';
import TiptapImage from '@tiptap/extension-image';
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import { ImageUploadClient } from '@spotify/confidence-backstage-image-upload';
import { CommentZone } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId, useIsEditorEditable } from '../../hooks';
import { blockIdAttribute } from '../../utils';
import { BlockReorderButtons } from '../BlockReorderButtons';
import { BlockCommentButton } from '../Comment';
import { ToolbarCard } from '../FloatingCard';
import { ToolbarButton } from '../ToolbarButton';
import { tiptapBlockMarginBottom, tiptapBlockMarginTop } from '../editorStyles';
import { createImageUploadPlugin } from './ImageUploadPlugin';
import { type ResizePosition, useFileUpload, useImageResize } from './hooks';

type ImageAlign = 'left' | 'center' | 'right';

const RESIZE_HANDLE_SIZE = 12;
const RESIZE_HANDLE_OFFSET = -6;

const RESIZE_POSITIONS: ResizePosition[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

const ImageWrapper = styled('div')({
  position: 'relative',
  display: 'inline-block',
  marginBottom: tiptapBlockMarginBottom,
  marginTop: tiptapBlockMarginTop,
  maxWidth: '100%',
  '&:hover .image-controls, & .image-controls:hover': {
    opacity: 1,
  },
});

const StyledImage = styled('img')({
  display: 'inline-block',
  maxWidth: '100%',
  height: 'auto',
  borderRadius: 'var(--encore-corner-radius-base)',
});

const ImagePlaceholder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--background-elevated-base)',
  borderRadius: 'var(--encore-corner-radius-base)',
  border: '1px dashed var(--decorative-subdued)',
  cursor: 'pointer',
  '&:hover': {
    borderColor: 'var(--essential-subdued)',
    backgroundColor: 'var(--background-tinted-highlight)',
  },
});

const PlaceholderContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--encore-spacing-base)',
  color: 'var(--text-subdued)',
  padding: 'var(--encore-spacing-base)',
  textAlign: 'center',
  width: '100%',
});

const ImageControls = styled(ToolbarCard)({
  position: 'absolute',
  top: '-6px',
  right: 0,
  transform: 'translateY(-100%)',
  zIndex: 1000,
  opacity: 0,
  marginBottom: 'var(--encore-spacing-tighter-2)',
  transition: 'opacity 0.2s',
});

const LoadingContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--encore-spacing-tighter-4)',
  color: 'var(--text-subdued)',
  minHeight: 20,
});

const ResizeHandle = styled('div')({
  position: 'absolute',
  width: `${RESIZE_HANDLE_SIZE}px`,
  height: `${RESIZE_HANDLE_SIZE}px`,
  backgroundColor: 'var(--text-base)',
  border: '2px solid var(--decorative-subdued)',
  borderRadius: '50%',
  opacity: 0,
  transition: 'opacity 0.2s',
  '.ProseMirror-selectednode &': {
    opacity: 1,
  },
  '&:hover': {
    backgroundColor: 'var(--text-subdued)',
  },
  '&[data-position="top-left"]': {
    top: `${RESIZE_HANDLE_OFFSET}px`,
    left: `${RESIZE_HANDLE_OFFSET}px`,
    cursor: 'nwse-resize',
  },
  '&[data-position="top-right"]': {
    top: `${RESIZE_HANDLE_OFFSET}px`,
    right: `${RESIZE_HANDLE_OFFSET}px`,
    cursor: 'nesw-resize',
  },
  '&[data-position="bottom-left"]': {
    bottom: `${RESIZE_HANDLE_OFFSET}px`,
    left: `${RESIZE_HANDLE_OFFSET}px`,
    cursor: 'nesw-resize',
  },
  '&[data-position="bottom-right"]': {
    bottom: `${RESIZE_HANDLE_OFFSET}px`,
    right: `${RESIZE_HANDLE_OFFSET}px`,
    cursor: 'nwse-resize',
  },
});

const HiddenFileInput = styled('input')({
  display: 'none',
});

const ImageComponent = ({
  node,
  deleteNode,
  updateAttributes,
  editor,
  extension,
  getPos,
}: NodeViewProps) => {
  const isEditable = useIsEditorEditable(editor);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const {
    src,
    alt,
    width,
    align,
    blockId,
    isUploading: isUploadingFromNode,
  } = node.attrs;
  const zoneId = useBlockZoneId('image', blockId, updateAttributes);

  const imageUploadClient = extension.options.imageUploadClient;

  const { isUploading: isUploadingFromHook, handleFileSelect } = useFileUpload({
    imageUploadClient,
    onUploadComplete: data => updateAttributes(data),
  });

  // Combine: could be uploading from drag/paste (node attr) OR click (hook)
  const isUploading = isUploadingFromNode || isUploadingFromHook;

  const { isResizing, handleResizeStart } = useImageResize({
    imageRef,
    onResizeComplete: dimensions => updateAttributes(dimensions),
  });

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDelete = useCallback(() => {
    deleteNode();
  }, [deleteNode]);

  const handleAlignChange = useCallback(
    (newAlign: ImageAlign) => {
      updateAttributes({ align: newAlign });
    },
    [updateAttributes],
  );

  const wrapWithZone = (children: React.ReactNode) =>
    zoneId ? (
      <CommentZone id={zoneId} component="div">
        {children}
      </CommentZone>
    ) : (
      children
    );

  if (!src) {
    return (
      <NodeViewWrapper
        data-align={align}
        style={{
          display: 'block',
          textAlign: align as CSSProperties['textAlign'],
        }}
      >
        {wrapWithZone(
          <ImageWrapper style={{ width: '100%' }}>
            {isEditable ? (
              <>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                />
                <ImagePlaceholder onClick={openFileDialog}>
                  <PlaceholderContent>
                    <RiImageAddLine size={32} />
                    <div>
                      {isUploading ? (
                        <LoadingContainer>
                          <CircularProgress
                            size={14}
                            style={{ marginRight: 6, verticalAlign: 'middle' }}
                          />
                          Uploading image...
                        </LoadingContainer>
                      ) : (
                        'Click to upload an image'
                      )}
                    </div>
                  </PlaceholderContent>
                </ImagePlaceholder>
                <div contentEditable={false}>
                  <ImageControls className="image-controls">
                    <Flex
                      display="flex"
                      style={{
                        gap: 'var(--encore-spacing-tighter-5)',
                      }}
                    >
                      <BlockReorderButtons getPos={getPos} editor={editor} />
                      <BlockCommentButton />
                      <Tooltip title="Delete image">
                        <ToolbarButton
                          aria-label="Delete image"
                          onClick={handleDelete}
                        >
                          <RiDeleteBinLine />
                        </ToolbarButton>
                      </Tooltip>
                    </Flex>
                  </ImageControls>
                </div>
              </>
            ) : (
              <ImagePlaceholder>
                <PlaceholderContent />
              </ImagePlaceholder>
            )}
          </ImageWrapper>,
        )}
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      data-align={align}
      style={{ display: 'block', textAlign: align }}
    >
      {wrapWithZone(
        <ImageWrapper>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
          />

          <StyledImage
            ref={imageRef}
            src={src}
            alt={alt || ''}
            style={{
              width: width ? `${width}px` : undefined,
              userSelect: isResizing ? 'none' : undefined,
            }}
          />

          {isEditable && (
            <>
              <div contentEditable={false}>
                <ImageControls className="image-controls">
                  <Flex
                    display="flex"
                    style={{
                      gap: 'var(--encore-spacing-tighter-5)',
                    }}
                  >
                    <BlockReorderButtons getPos={getPos} editor={editor} />
                    <Tooltip title="Align left">
                      <ToolbarButton
                        aria-label="Align left"
                        onClick={() => handleAlignChange('left')}
                        active={align === 'left'}
                      >
                        <RiAlignItemLeftLine />
                      </ToolbarButton>
                    </Tooltip>
                    <Tooltip title="Align center">
                      <ToolbarButton
                        aria-label="Align center"
                        onClick={() => handleAlignChange('center')}
                        active={align === 'center'}
                      >
                        <RiAlignItemHorizontalCenterLine />
                      </ToolbarButton>
                    </Tooltip>
                    <Tooltip title="Align right">
                      <ToolbarButton
                        aria-label="Align right"
                        onClick={() => handleAlignChange('right')}
                        active={align === 'right'}
                      >
                        <RiAlignItemRightLine />
                      </ToolbarButton>
                    </Tooltip>
                    <BlockCommentButton />
                    <Tooltip title="Replace image">
                      <ToolbarButton
                        aria-label="Replace image"
                        onClick={openFileDialog}
                      >
                        <RiImageAddLine />
                      </ToolbarButton>
                    </Tooltip>
                    <Tooltip title="Delete image">
                      <ToolbarButton
                        aria-label="Delete image"
                        onClick={handleDelete}
                      >
                        <RiDeleteBinLine />
                      </ToolbarButton>
                    </Tooltip>
                  </Flex>
                </ImageControls>
              </div>

              {RESIZE_POSITIONS.map(position => (
                <ResizeHandle
                  key={position}
                  data-position={position}
                  onMouseDown={e => handleResizeStart(e, position)}
                />
              ))}
            </>
          )}
        </ImageWrapper>,
      )}
    </NodeViewWrapper>
  );
};

interface ImageExtensionOptions {
  imageUploadClient?: ImageUploadClient;
  uploadFile?: (file: File) => Promise<string>;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  onUploadError?: (error: Error) => void;
}

export const Image = TiptapImage.extend<ImageExtensionOptions>({
  inline: false,
  group: 'block',

  addOptions() {
    return {
      ...this.parent?.(),
      imageUploadClient: undefined,
      uploadFile: undefined,
      onUploadStart: undefined,
      onUploadComplete: undefined,
      onUploadError: undefined,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
      align: {
        default: 'left',
        renderHTML: attributes => ({
          'data-align': attributes.align,
        }),
        parseHTML: element => element.getAttribute('data-align') || 'left',
      },
      blockId: blockIdAttribute,
      isUploading: {
        default: false,
        renderHTML: () => ({}), // Don't persist to HTML
        parseHTML: () => false,
      },
      uploadId: {
        default: null,
        renderHTML: () => ({}), // Don't persist to HTML
        parseHTML: () => null,
      },
    };
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(ImageComponent);
  },

  addProseMirrorPlugins() {
    if (!this.options.uploadFile) {
      return [];
    }
    return [
      createImageUploadPlugin({
        uploadFile: this.options.uploadFile,
        onUploadStart: this.options.onUploadStart,
        onUploadComplete: this.options.onUploadComplete,
        onUploadError: this.options.onUploadError,
      }),
    ];
  },
});
